/**
 * TIC 커서가 가리키는 스캔 — 2026-09-03 (운영자 결정 1-(c) · 2-(b)).
 *
 * ## 무엇이 문제였나 (실측)
 *
 * TIC 패널 폭은 303 CSS 픽셀인데 원자료는 4,028점이다. **화면 1픽셀 안에
 * 원자료가 13.3점** 겹친다. 그림은 그 13점의 위아래를 세로 잉크로 다 그리는데,
 * 종전 커서는 그중 **커서에 가장 가까운 1점**의 값을 숫자로 보여줬다.
 * 봉우리 근처에서는 그 1점이 밑동일 확률이 높다.
 *
 * 가장 높은 봉우리(RT 2.841, 실제 세기 1,314,973)에서 1픽셀씩 옮기며 읽으면:
 *
 *   dx=-1  RT 2.698  Intensity     53,982
 *   dx= 0  RT 2.775  Intensity     72,666   ← 봉우리 자리인데 18배 낮다
 *   dx=+1  RT 2.853  Intensity    936,475   ← 1픽셀 옆
 *
 * 숫자가 조작된 것은 아니다 — 전부 실제로 측정된 스캔 값이다. 다만 증거
 * 화면에서 **보는 것과 읽는 것이 달랐다.**
 *
 * ## 어떻게 바꾸는가
 *
 * 커서가 놓인 **화면 1픽셀 폭 안의 실측 스캔 중 가장 높은 것**을 가리킨다.
 * 여전히 없는 숫자를 만들지 않는다(언제나 실측 스캔 하나의 값이다). 확대하면
 * 1픽셀 = 스캔 1개가 되어 차이가 저절로 사라진다(실측: RT 창 1.5분 이하에서 0).
 *
 * 운영자가 처음 제안한 "가로선을 그 값에 고정"은 채택하지 않았다 — 그러면 위의
 * 18배 어긋남이 화면을 가로지르는 선으로 **확정**되고, 마우스 1픽셀에 선이
 * 위아래로 튄다. 뜻 없는 가로선(마우스 높이를 따라갈 뿐)은 끄고, 곡선 위
 * 커서 표식이 그 스캔을 가리키게 하는 쪽을 택했다.
 */

/**
 * 주어진 x 구간 안에서 값이 가장 큰 점의 인덱스.
 *
 * @param xs 오름차순 x 값(RT).
 * @param ys y 값. 결측은 `null` 이며 후보에서 제외한다 — 없는 신호를 가리키지 않는다.
 * @param xMin 구간 시작(포함).
 * @param xMax 구간 끝(포함).
 * @param fallback 구간에 쓸 점이 없을 때 돌려줄 인덱스(uPlot 의 최근접 판단).
 */
export function maxIndexInPixelColumn(
  xs: readonly number[],
  ys: readonly (number | null | undefined)[],
  xMin: number,
  xMax: number,
  fallback: number,
): number {
  if (xs.length === 0) return fallback;

  // 구간의 첫 인덱스를 이진 탐색으로 찾는다 — 원자료 전량(수천~수만 점)을
  // 마우스가 움직일 때마다 훑으면 안 된다.
  let lo = 0;
  let hi = xs.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (xs[mid] < xMin) lo = mid + 1;
    else hi = mid;
  }

  // 구간이 데이터 오른쪽 바깥이면 이진 탐색이 마지막 인덱스에 멈춘다 —
  // 그대로 두면 구간 밖의 점을 집는다.
  if (xs[lo] < xMin) return fallback;

  let bestIndex = -1;
  let bestValue = Number.NEGATIVE_INFINITY;
  for (let i = lo; i < xs.length && xs[i] <= xMax; i += 1) {
    const value = ys[i];
    if (value == null || !Number.isFinite(value)) continue;
    // 동점이면 먼저 나온(더 이른 RT) 스캔을 유지한다 — 커서가 흔들리지 않게.
    if (value > bestValue) {
      bestValue = value;
      bestIndex = i;
    }
  }

  return bestIndex === -1 ? fallback : bestIndex;
}

/**
 * 봉우리를 쫓아갈 수 있는 최대 거리(화면 픽셀).
 *
 * 표식이 커서에서 너무 멀어지면 무엇을 가리키는지 알 수 없다. 픽셀 기준이라
 * 확대·축소와 무관하게 "화면에서 이만큼"이 유지된다.
 */
export const CURSOR_PEAK_REACH_PX = 10;

/**
 * 골짜기 판정 — 지금까지 만난 최고 칸의 이 비율 아래로 떨어지면 **다른 봉우리**로
 * 본다. 없으면 멀리 있는 더 큰 봉우리로 튀고, 너무 엄하면 옆 봉우리에 못 닿는다.
 */
export const PEAK_VALLEY_RATIO = 0.5;

/** 탐색을 화면에 보이는 x 범위로 자른다. 없으면 자르지 않는다. */
export interface PeakSearchWindow {
  xMin: number;
  xMax: number;
}

/**
 * 커서 근처 **봉우리의 꼭대기** 인덱스 — 커서 숫자가 아니라 **두 번째 읽기**다.
 *
 * ## 왜 두 번째인가 (2026-09-06)
 *
 * 커서에 붙는 숫자는 `maxIndexInPixelColumn` 이 정한다. 그 규칙은 보고 지점이
 * **언제나 커서 자기 픽셀 열 안**이라는 보장을 준다(최대 변위 0.5픽셀). 증거
 * 화면에서 "이 숫자는 내가 가리킨 자리의 값"이 성립하는 근거다.
 *
 * 이 함수는 최대 `reachPx` 픽셀 떨어진 스캔을 돌려줄 수 있다 — 프로덕션 화면
 * 기준 **약 3.3분**. 그 값을 커서 숫자로 쓰면 위 보장이 깨지고, 화면 문구가
 * 거짓이 된다. 그래서 **이름을 붙인 별도 줄**로 보여준다.
 *
 * ## 화면 해상도에서 찾는다 (핵심)
 *
 * 원자료 해상도로 오르내리면 **사람이 볼 수 없는 골짜기**에 막힌다. 실측(로컬
 * 분석 326): RT 2.728(142,185)과 2.841(1,314,973)은 화면에서 **1.5픽셀** 떨어진
 * 두 봉우리이고 사이 골짜기는 시작값의 47%다. 데이터로는 두 봉우리지만
 * **화면에서는 하나의 스파이크**여서, 그 골짜기에 막혀 바로 옆 거대한 봉우리를
 * 못 잡았다. 그래서 **화면 1픽셀을 한 칸으로 묶어** 칸의 최댓값으로 오르내린다.
 *
 * ## 멈추는 조건
 *
 *   · **골짜기** — 지금까지 최고 칸의 `valleyRatio` 아래로 떨어지면 다른 봉우리
 *   · **결측** — 값이 하나도 없는 칸을 만나면 끝(없는 신호를 잇지 않는다)
 *   · **상한** — `reachPx` 칸을 넘지 않는다
 *   · **보이는 범위** — `window` 밖으로 나가지 않는다(화면에 없는 스캔을
 *     보고하지 않는다)
 *   · **배열 끝**
 *
 * 양쪽 후보 중 **더 높은 것**, 같으면 **커서에 가까운** 칸, 그것도 같으면 더
 * **이른 RT** 를 고른다 — 같은 자리에서 답이 흔들리지 않게.
 *
 * @param xs 오름차순 x 값(RT).
 * @param ys y 값. 결측은 `null`.
 * @param start 커서에 가장 가까운 점의 인덱스.
 * @param perPx 화면 1픽셀이 담는 x 폭.
 * @param reachPx 쫓아갈 수 있는 최대 픽셀 수.
 * @param valleyRatio 골짜기 판정 비율.
 * @param window 보이는 x 범위. 지정하면 그 밖으로 나가지 않는다.
 */
export function peakIndexNear(
  xs: readonly number[],
  ys: readonly (number | null | undefined)[],
  start: number,
  perPx: number,
  reachPx: number = CURSOR_PEAK_REACH_PX,
  valleyRatio: number = PEAK_VALLEY_RATIO,
  window?: PeakSearchWindow,
): number {
  if (start < 0 || start >= xs.length) return start;
  if (!(perPx > 0) || !Number.isFinite(perPx)) return start;

  const originX = xs[start];
  const visible = (i: number) =>
    window == null || (xs[i] >= window.xMin && xs[i] <= window.xMax);
  /** 커서 칸(0)에서 몇 칸 떨어졌는가. */
  const columnOf = (i: number) => Math.floor(Math.abs(xs[i] - originX) / perPx);

  // 커서 칸(0) 안의 최댓값 — 커서 숫자와 같은 규칙이 출발점이다.
  let originIndex = -1;
  let originValue = Number.NEGATIVE_INFINITY;
  const considerOrigin = (i: number) => {
    if (!visible(i)) return;
    const value = ys[i];
    if (value == null || !Number.isFinite(value)) return;
    // 동점이면 더 이른 RT 를 유지한다(> 이지 >= 가 아니다).
    if (value > originValue) {
      originValue = value;
      originIndex = i;
    }
  };
  // 이른 RT 우선을 위해 왼쪽 끝부터 훑는다.
  const originIdx: number[] = [start];
  for (const step of [1, -1] as const) {
    for (let i = start + step; i >= 0 && i < xs.length; i += step) {
      if (columnOf(i) !== 0) break;
      originIdx.push(i);
    }
  }
  originIdx.sort((a, b) => a - b);
  for (const i of originIdx) considerOrigin(i);
  if (originIndex === -1) return start;

  /** 한 방향으로 칸별 최고점을 모은다. 칸 c 는 결과의 c-1 번째. */
  const scan = (step: 1 | -1) => {
    const columns: ({ value: number; index: number } | null)[] = [];
    for (let i = start + step; i >= 0 && i < xs.length; i += step) {
      if (!visible(i)) break;
      const column = columnOf(i);
      if (column > reachPx) break;
      if (column === 0) continue; // 커서 칸은 origin 이 담당한다
      while (columns.length < column) columns.push(null);
      const value = ys[i];
      if (value == null || !Number.isFinite(value)) continue;
      const slot = columns[column - 1];
      if (!slot || value > slot.value)
        columns[column - 1] = { value, index: i };
    }
    return columns;
  };

  let bestIndex = originIndex;
  let bestValue = originValue;
  let bestColumn = 0;

  for (const step of [1, -1] as const) {
    let runningMax = originValue;
    for (const [offset, cell] of scan(step).entries()) {
      // 값이 하나도 없는 칸 = 결측 구간. 여기서 끊는다.
      if (!cell) break;
      if (cell.value < runningMax * valleyRatio) break;
      if (cell.value > runningMax) runningMax = cell.value;

      const column = offset + 1;
      const better =
        cell.value > bestValue ||
        (cell.value === bestValue &&
          (column < bestColumn ||
            (column === bestColumn && cell.index < bestIndex)));
      if (better) {
        bestIndex = cell.index;
        bestValue = cell.value;
        bestColumn = column;
      }
    }
  }

  return bestIndex;
}

/**
 * 커서 값이 무엇인지 화면에 밝히는 문구.
 *
 * 표시 규칙이 바뀌었으면 화면에 쓰여 있어야 한다 — 증거 화면에서 숫자의 뜻을
 * 사람이 추측하게 두면 안 된다.
 *
 * @param scansPerPixel 지금 보고 있는 구간에서 화면 1픽셀에 겹치는 원자료 점 수.
 */
export function ticCursorNote(scansPerPixel: number): string {
  // 2026-09-06: 칩에 **두 번째 줄**("가까운 봉우리")이 붙을 수 있으므로 두 숫자를
  // 각각 설명한다. 커서 값의 뜻은 바뀌지 않았다 — 언제나 커서가 놓인 화면 1픽셀
  // 안의 실측값이다. 봉우리 값은 최대 10픽셀 떨어진 다른 스캔일 수 있고, 그
  // 사실이 이름("가까운 봉우리")과 이 문구 양쪽에 쓰여 있어야 한다.
  const peak =
    " · '가까운 봉우리'는 커서에서 최대 10픽셀 안의 다른 스캔일 수 있습니다";
  if (scansPerPixel < 1.5) {
    return `커서 값은 그 스캔의 실측값입니다${peak}`;
  }
  return `커서 값은 화면 1픽셀 폭(원자료 약 ${Math.round(scansPerPixel)}점) 안의 실측 스캔 중 최댓값입니다 — 확대하면 스캔 1개의 값이 됩니다${peak}`;
}
